import type { SupportedLocale } from '@erp/shared';
import { lexwareArchiveImportTranslations } from './lexware-archive-import-i18n';

const en = {
  import: lexwareArchiveImportTranslations.en,
  nativeTitle: 'ERP documents',
  categoryTitle: 'Imported documents',
  categoryDescription:
    'Search and dates also apply to the original documents in this category. Open a document to view its protected contents and original files.',
  categoryOperativeFilters:
    'Status and payment filters apply only to ERP transactions. Imported documents retain their original statuses and do not create current receivables.',
  categorySort:
    'Imported documents are sorted by document date, newest first. Sorting by document date or number applies to both lists.',
  noAccess: 'Read permission for historical documents is required.',
  title: 'Historical documents',
  origin: 'Historical document · {{system}}',
  customerDescription:
    'Original documents for this partner, across all years. Historical amounts do not increase current sales or outstanding balances.',
  description: 'Original document snapshots from the previous system.',
  readOnly:
    'Historical documents are protected. Continuing an open transaction requires a separately reviewed transition.',
  search: 'Search document number',
  lexwareTitle: 'Lexware historical documents',
  lexwareReadOnly:
    'Standalone read-only archive. No links to current customer, product or accounting data; viewing creates no transactions or messages.',
  lexwareSearch: 'Search old customer or document number, name or text',
  allSources: 'All source systems',
  partnerNumber: 'Old customer number',
  historicalSourceTenant: 'Historical source tenant',
  sourceTenantPlaceholder: 'Source tenant (e.g. F3 / F4)',
  pdfFullText: 'Full text from the original PDF',
  pdfTextStatus: 'PDF text',
  pdfTextExcerpt: 'Text excerpt from the original PDF',
  textStatuses: {
    EXTRACTED: 'Text available',
    EMPTY: 'No text found',
    ERROR: 'Text could not be extracted',
    TRUNCATED: 'Text excerpt only',
  },
  pdfTextHint:
    'Extracted text retains the original line breaks. Consult the original PDF for missing or uncertain information.',
  allKinds: 'All document types',
  kind: 'Document type',
  number: 'Document number',
  date: 'Document date',
  partner: 'Historical partner',
  status: 'Original status',
  net: 'Net',
  tax: 'Tax',
  gross: 'Gross',
  loading: 'Loading historical documents',
  error: 'Historical documents could not be loaded.',
  retry: 'Reload',
  empty: 'No historical documents found.',
  reset: 'Clear filters',
  close: 'Close',
  help: 'Help with historical documents',
  previous: 'Previous',
  next: 'Next',
  page: 'Page {{page}} of {{total}}',
  unknown: 'Not recorded',
  emptyValue: 'Empty',
  open: 'Open in the source',
  closed: 'Closed in the source',
  unknownOpen: 'Open state not classified',
  code: 'Status code {{code}}',
  items: 'Items',
  position: 'Position',
  descriptionColumn: 'Original description',
  quantity: 'Quantity',
  unit: 'Unit',
  unitPrice: 'Unit net price',
  discount: 'Discount',
  fields: 'Historical custom fields',
  fieldsRestricted: 'Additional permissions are required for internal custom fields.',
  payments: 'Historical payments',
  paymentsRestricted: 'Additional permissions are required for payment details.',
  relations: 'Document relationships',
  originals: 'Original files and attachments',
  noOriginals: 'No original file linked.',
  download: 'Download {{name}}',
  downloadError: 'The original could not be downloaded.',
  downloaded: 'Original downloaded.',
  original: 'Original',
  attachment: 'Source attachment',
  emptyFile: 'Empty source file',
  provenance: 'Source evidence',
  sourceSystem: 'Source system',
  sourceInstance: 'Source installation',
  sourceTenant: 'Source tenant',
  sourceId: 'Source ID',
  back: 'All historical documents',
  headerTexts: 'Original texts',
  openPartner: 'Open partner record',
  statusDimensions: 'Source status dimensions',
  paymentOpen: 'Source payment status: open',
  paymentClosed: 'Source payment status: settled or written off',
  statusAxes: {
    CHANNEL: 'Origin channel',
    DELIVERY: 'Delivery',
    INVOICING: 'Invoicing',
    PAYMENT: 'Payment',
  },
  statusMeanings: {
    UNKNOWN: 'Meaning unresolved',
    FULLY_DELIVERED: 'Fully delivered according to source header',
    PAYMENT_OPEN: 'Open',
    PAYMENT_PARTIAL: 'Partly paid',
    PAYMENT_PAID: 'Fully paid',
    PAYMENT_REVIEW: 'Under review',
    PAYMENT_WRITTEN_OFF: 'Written off',
  },
  kinds: {
    CORRECTION_INVOICE: 'Correction invoice',
    INVOICE_UNCLASSIFIED: 'Invoice document · type unresolved',
    OFFER: 'Offer',
    ORDER: 'Order',
    DELIVERY: 'Delivery note',
    INVOICE: 'Invoice',
    CREDIT_NOTE: 'Credit note',
    CANCELLATION: 'Cancellation',
    PURCHASE_ORDER: 'Purchase order',
    PURCHASE_INVOICE: 'Purchase invoice',
  },
};
export const legacyDocumentTranslations: Record<SupportedLocale, typeof en> = {
  en,
  de: {
    import: lexwareArchiveImportTranslations.de,
    nativeTitle: 'ERP-Belege',
    categoryTitle: 'Übernommene Belege',
    categoryDescription:
      'Suche und Datumswahl gelten auch für die Originalbelege dieser Kategorie. Öffnen Sie einen Beleg für die geschützten Inhalte und Originaldateien.',
    categoryOperativeFilters:
      'Status- und Zahlungsfilter gelten nur für ERP-Vorgänge. Übernommene Belege behalten ihre ursprünglichen Statusangaben und erzeugen keine aktuellen Forderungen.',
    categorySort:
      'Übernommene Belege sind nach Belegdatum absteigend sortiert. Die Sortierung nach Belegdatum oder Belegnummer gilt für beide Listen.',
    noAccess: 'Für Altbelege fehlen die Leserechte.',
    title: 'Altbelege',
    origin: 'Altbeleg · {{system}}',
    customerDescription:
      'Originalbelege dieses Geschäftspartners aus allen Jahren. Historische Beträge erhöhen weder laufende Umsätze noch offene Posten.',
    description: 'Ursprüngliche Beleginhalte aus dem bisherigen System.',
    readOnly:
      'Altbelege sind geschützt. Die Weiterbearbeitung offener Vorgänge erfordert einen gesondert geprüften Übergang.',
    search: 'Belegnummer suchen',
    lexwareTitle: 'Lexware Altbelege',
    lexwareReadOnly:
      'Eigenständiges, schreibgeschütztes Archiv. Keine Verknüpfung mit aktuellen Kunden-, Artikel- oder Buchhaltungsdaten; die Anzeige erzeugt keine Vorgänge oder Nachrichten.',
    lexwareSearch: 'Alte Kunden- oder Belegnummer, Name oder Text suchen',
    allSources: 'Alle Quellsysteme',
    partnerNumber: 'Alte Kundennummer',
    historicalSourceTenant: 'Historischer Quellmandant',
    sourceTenantPlaceholder: 'Quellmandant (z. B. F3 / F4)',
    pdfFullText: 'Volltext aus dem Original-PDF',
    pdfTextStatus: 'PDF-Text',
    pdfTextExcerpt: 'Textauszug aus dem Original-PDF',
    textStatuses: {
      EXTRACTED: 'Text verfügbar',
      EMPTY: 'Kein Text gefunden',
      ERROR: 'Text konnte nicht ausgelesen werden',
      TRUNCATED: 'Nur Textauszug verfügbar',
    },
    pdfTextHint:
      'Der extrahierte Text erhält die ursprünglichen Zeilenumbrüche. Prüfen Sie fehlende oder unsichere Angaben im Original-PDF.',
    allKinds: 'Alle Belegarten',
    kind: 'Belegart',
    number: 'Belegnummer',
    date: 'Belegdatum',
    partner: 'Historischer Geschäftspartner',
    status: 'Originalstatus',
    net: 'Netto',
    tax: 'Steuer',
    gross: 'Brutto',
    loading: 'Altbelege werden geladen',
    error: 'Altbelege konnten nicht geladen werden.',
    retry: 'Erneut laden',
    empty: 'Keine Altbelege gefunden.',
    reset: 'Filter zurücksetzen',
    close: 'Schließen',
    help: 'Hilfe zu Altbelegen',
    previous: 'Zurück',
    next: 'Weiter',
    page: 'Seite {{page}} von {{total}}',
    unknown: 'Nicht gespeichert',
    emptyValue: 'Leer',
    open: 'In der Quelle offen',
    closed: 'In der Quelle abgeschlossen',
    unknownOpen: 'Offenstatus nicht eingeordnet',
    code: 'Statuscode {{code}}',
    items: 'Positionen',
    position: 'Position',
    descriptionColumn: 'Originalbeschreibung',
    quantity: 'Menge',
    unit: 'Einheit',
    unitPrice: 'Nettoeinzelpreis',
    discount: 'Rabatt',
    fields: 'Historische Zusatzfelder',
    fieldsRestricted: 'Für interne Zusatzfelder sind weitere Leserechte erforderlich.',
    payments: 'Historische Zahlungen',
    paymentsRestricted: 'Für Zahlungsdetails sind weitere Leserechte erforderlich.',
    relations: 'Belegbeziehungen',
    originals: 'Originaldateien und Anlagen',
    noOriginals: 'Keine Originaldatei verknüpft.',
    download: '{{name}} herunterladen',
    downloadError: 'Das Original konnte nicht heruntergeladen werden.',
    downloaded: 'Original heruntergeladen.',
    original: 'Original',
    attachment: 'Quellanlage',
    emptyFile: 'Leere Quelldatei',
    provenance: 'Herkunftsnachweis',
    sourceSystem: 'Quellsystem',
    sourceInstance: 'Quellinstallation',
    sourceTenant: 'Quellmandant',
    sourceId: 'Quell-ID',
    back: 'Alle Altbelege',
    headerTexts: 'Originaltexte',
    openPartner: 'Geschäftspartnerakte öffnen',
    statusDimensions: 'Statusbereiche der Quelle',
    paymentOpen: 'Quell-Zahlungsstatus: offen',
    paymentClosed: 'Quell-Zahlungsstatus: beglichen oder ausgebucht',
    statusAxes: {
      CHANNEL: 'Eingangskanal',
      DELIVERY: 'Lieferung',
      INVOICING: 'Fakturierung',
      PAYMENT: 'Zahlung',
    },
    statusMeanings: {
      UNKNOWN: 'Bedeutung ungeklärt',
      FULLY_DELIVERED: 'Laut Quellkopf komplett geliefert',
      PAYMENT_OPEN: 'Offen',
      PAYMENT_PARTIAL: 'Teilweise bezahlt',
      PAYMENT_PAID: 'Vollständig bezahlt',
      PAYMENT_REVIEW: 'In Prüfung',
      PAYMENT_WRITTEN_OFF: 'Uneinbringlich',
    },
    kinds: {
      CORRECTION_INVOICE: 'Korrekturrechnung',
      INVOICE_UNCLASSIFIED: 'Rechnungsbeleg · Art ungeklärt',
      OFFER: 'Angebot',
      ORDER: 'Auftrag',
      DELIVERY: 'Lieferschein',
      INVOICE: 'Rechnung',
      CREDIT_NOTE: 'Gutschrift',
      CANCELLATION: 'Storno',
      PURCHASE_ORDER: 'Bestellung',
      PURCHASE_INVOICE: 'Eingangsrechnung',
    },
  },
  fr: {
    import: lexwareArchiveImportTranslations.fr,
    nativeTitle: 'Documents ERP',
    categoryTitle: 'Documents importés',
    categoryDescription:
      'La recherche et les dates s’appliquent aussi aux documents originaux de cette catégorie. Ouvrez un document pour consulter son contenu protégé et ses fichiers originaux.',
    categoryOperativeFilters:
      'Les filtres de statut et de paiement s’appliquent uniquement aux opérations ERP. Les documents importés conservent leurs statuts originaux et ne créent pas de créances actuelles.',
    categorySort:
      'Les documents importés sont classés par date décroissante. Le tri par date ou numéro de document s’applique aux deux listes.',
    noAccess: 'Vous ne disposez pas des droits de lecture des documents historiques.',
    title: 'Documents historiques',
    origin: 'Document historique · {{system}}',
    customerDescription:
      'Documents originaux de ce partenaire, toutes années confondues. Les montants historiques ne majorent ni les ventes courantes ni les soldes ouverts.',
    description: 'Contenu original des documents du système précédent.',
    readOnly:
      'Les documents historiques sont protégés. La poursuite d’une opération ouverte nécessite une transition vérifiée séparément.',
    search: 'Rechercher un numéro de document',
    lexwareTitle: 'Documents historiques Lexware',
    lexwareReadOnly:
      'Archive indépendante en lecture seule. Aucun lien vers les données actuelles de clients, d’articles ou de comptabilité ; la consultation ne crée aucune opération ni message.',
    lexwareSearch: 'Rechercher ancien numéro de client ou de document, nom ou texte',
    allSources: 'Tous les systèmes source',
    partnerNumber: 'Ancien numéro de client',
    historicalSourceTenant: 'Dossier source historique',
    sourceTenantPlaceholder: 'Dossier source (ex. F3 / F4)',
    pdfFullText: 'Texte intégral du PDF original',
    pdfTextStatus: 'Texte PDF',
    pdfTextExcerpt: 'Extrait de texte du PDF original',
    textStatuses: {
      EXTRACTED: 'Texte disponible',
      EMPTY: 'Aucun texte trouvé',
      ERROR: 'Impossible d’extraire le texte',
      TRUNCATED: 'Extrait de texte uniquement',
    },
    pdfTextHint:
      'Le texte extrait conserve les sauts de ligne d’origine. Vérifiez les informations absentes ou incertaines dans le PDF original.',
    allKinds: 'Tous les types',
    kind: 'Type de document',
    number: 'Numéro',
    date: 'Date du document',
    partner: 'Partenaire historique',
    status: 'Statut original',
    net: 'Hors taxes',
    tax: 'Taxe',
    gross: 'TTC',
    loading: 'Chargement des documents historiques',
    error: 'Impossible de charger les documents historiques.',
    retry: 'Recharger',
    empty: 'Aucun document historique trouvé.',
    reset: 'Réinitialiser les filtres',
    close: 'Fermer',
    help: 'Aide sur les documents historiques',
    previous: 'Précédent',
    next: 'Suivant',
    page: 'Page {{page}} sur {{total}}',
    unknown: 'Non enregistré',
    emptyValue: 'Vide',
    open: 'Ouvert dans la source',
    closed: 'Clôturé dans la source',
    unknownOpen: 'État ouvert non classé',
    code: 'Code de statut {{code}}',
    items: 'Lignes',
    position: 'Position',
    descriptionColumn: 'Description originale',
    quantity: 'Quantité',
    unit: 'Unité',
    unitPrice: 'Prix unitaire HT',
    discount: 'Remise',
    fields: 'Champs personnalisés historiques',
    fieldsRestricted: 'Des droits supplémentaires sont nécessaires pour les champs internes.',
    payments: 'Paiements historiques',
    paymentsRestricted:
      'Des droits supplémentaires sont nécessaires pour les détails des paiements.',
    relations: 'Relations entre documents',
    originals: 'Fichiers originaux et pièces jointes',
    noOriginals: 'Aucun fichier original lié.',
    download: 'Télécharger {{name}}',
    downloadError: 'Impossible de télécharger l’original.',
    downloaded: 'Original téléchargé.',
    original: 'Original',
    attachment: 'Pièce jointe source',
    emptyFile: 'Fichier source vide',
    provenance: 'Preuve d’origine',
    sourceSystem: 'Système source',
    sourceInstance: 'Installation source',
    sourceTenant: 'Dossier source',
    sourceId: 'Identifiant source',
    back: 'Tous les documents historiques',
    headerTexts: 'Textes originaux',
    openPartner: 'Ouvrir la fiche partenaire',
    statusDimensions: 'Dimensions du statut source',
    paymentOpen: 'Statut de paiement source : ouvert',
    paymentClosed: 'Statut de paiement source : réglé ou passé en perte',
    statusAxes: {
      CHANNEL: 'Canal d’origine',
      DELIVERY: 'Livraison',
      INVOICING: 'Facturation',
      PAYMENT: 'Paiement',
    },
    statusMeanings: {
      UNKNOWN: 'Signification indéterminée',
      FULLY_DELIVERED: 'Livraison complète selon l’en-tête source',
      PAYMENT_OPEN: 'Ouvert',
      PAYMENT_PARTIAL: 'Partiellement payé',
      PAYMENT_PAID: 'Entièrement payé',
      PAYMENT_REVIEW: 'En cours de vérification',
      PAYMENT_WRITTEN_OFF: 'Passé en perte',
    },
    kinds: {
      CORRECTION_INVOICE: 'Facture rectificative',
      INVOICE_UNCLASSIFIED: 'Document de facturation · type indéterminé',
      OFFER: 'Devis',
      ORDER: 'Commande',
      DELIVERY: 'Bon de livraison',
      INVOICE: 'Facture',
      CREDIT_NOTE: 'Avoir',
      CANCELLATION: 'Annulation',
      PURCHASE_ORDER: 'Commande fournisseur',
      PURCHASE_INVOICE: 'Facture fournisseur',
    },
  },
  tr: {
    import: lexwareArchiveImportTranslations.tr,
    nativeTitle: 'ERP belgeleri',
    categoryTitle: 'Aktarılan belgeler',
    categoryDescription:
      'Arama ve tarihler bu kategorideki özgün belgelere de uygulanır. Korunan içeriği ve özgün dosyaları görmek için belgeyi açın.',
    categoryOperativeFilters:
      'Durum ve ödeme filtreleri yalnızca ERP işlemlerine uygulanır. Aktarılan belgeler özgün durumlarını korur ve güncel alacak oluşturmaz.',
    categorySort:
      'Aktarılan belgeler en yeni belge tarihi önce olacak şekilde sıralanır. Belge tarihi veya numarasına göre sıralama her iki listeye uygulanır.',
    noAccess: 'Geçmiş belgeler için okuma yetkisi gereklidir.',
    title: 'Geçmiş belgeler',
    origin: 'Geçmiş belge · {{system}}',
    customerDescription:
      'Bu iş ortağının tüm yıllara ait özgün belgeleri. Geçmiş tutarlar güncel satışları veya açık bakiyeleri artırmaz.',
    description: 'Önceki sistemdeki belgelerin özgün içeriği.',
    readOnly:
      'Geçmiş belgeler korunur. Açık işlemlere devam etmek için ayrıca incelenmiş bir geçiş gerekir.',
    search: 'Belge numarası ara',
    lexwareTitle: 'Lexware geçmiş belgeleri',
    lexwareReadOnly:
      'Bağımsız, salt okunur arşiv. Güncel müşteri, ürün veya muhasebe verilerine bağlantı yoktur; görüntüleme işlem veya mesaj oluşturmaz.',
    lexwareSearch: 'Eski müşteri veya belge numarası, ad veya metin ara',
    allSources: 'Tüm kaynak sistemler',
    partnerNumber: 'Eski müşteri numarası',
    historicalSourceTenant: 'Geçmiş kaynak firma',
    sourceTenantPlaceholder: 'Kaynak firma (ör. F3 / F4)',
    pdfFullText: 'Özgün PDF’nin tam metni',
    pdfTextStatus: 'PDF metni',
    pdfTextExcerpt: 'Özgün PDF’den metin alıntısı',
    textStatuses: {
      EXTRACTED: 'Metin mevcut',
      EMPTY: 'Metin bulunamadı',
      ERROR: 'Metin çıkarılamadı',
      TRUNCATED: 'Yalnızca metin alıntısı',
    },
    pdfTextHint:
      'Çıkarılan metin özgün satır sonlarını korur. Eksik veya belirsiz bilgileri özgün PDF’den kontrol edin.',
    allKinds: 'Tüm belge türleri',
    kind: 'Belge türü',
    number: 'Belge numarası',
    date: 'Belge tarihi',
    partner: 'Geçmiş iş ortağı',
    status: 'Özgün durum',
    net: 'Net',
    tax: 'Vergi',
    gross: 'Brüt',
    loading: 'Geçmiş belgeler yükleniyor',
    error: 'Geçmiş belgeler yüklenemedi.',
    retry: 'Yeniden yükle',
    empty: 'Geçmiş belge bulunamadı.',
    reset: 'Filtreleri sıfırla',
    close: 'Kapat',
    help: 'Geçmiş belge yardımı',
    previous: 'Önceki',
    next: 'Sonraki',
    page: 'Sayfa {{page}} / {{total}}',
    unknown: 'Kaydedilmemiş',
    emptyValue: 'Boş',
    open: 'Kaynakta açık',
    closed: 'Kaynakta kapalı',
    unknownOpen: 'Açıklık durumu sınıflandırılmamış',
    code: 'Durum kodu {{code}}',
    items: 'Kalemler',
    position: 'Sıra',
    descriptionColumn: 'Özgün açıklama',
    quantity: 'Miktar',
    unit: 'Birim',
    unitPrice: 'Net birim fiyatı',
    discount: 'İndirim',
    fields: 'Geçmiş özel alanlar',
    fieldsRestricted: 'İç özel alanlar için ek okuma yetkileri gerekir.',
    payments: 'Geçmiş ödemeler',
    paymentsRestricted: 'Ödeme ayrıntıları için ek okuma yetkileri gerekir.',
    relations: 'Belge ilişkileri',
    originals: 'Özgün dosyalar ve ekler',
    noOriginals: 'Bağlı özgün dosya yok.',
    download: '{{name}} indir',
    downloadError: 'Özgün dosya indirilemedi.',
    downloaded: 'Özgün dosya indirildi.',
    original: 'Özgün dosya',
    attachment: 'Kaynak eki',
    emptyFile: 'Boş kaynak dosyası',
    provenance: 'Kaynak kanıtı',
    sourceSystem: 'Kaynak sistem',
    sourceInstance: 'Kaynak kurulum',
    sourceTenant: 'Kaynak firma',
    sourceId: 'Kaynak kimliği',
    back: 'Tüm geçmiş belgeler',
    headerTexts: 'Özgün metinler',
    openPartner: 'İş ortağı kaydını aç',
    statusDimensions: 'Kaynak durum alanları',
    paymentOpen: 'Kaynak ödeme durumu: açık',
    paymentClosed: 'Kaynak ödeme durumu: ödenmiş veya silinmiş',
    statusAxes: {
      CHANNEL: 'Giriş kanalı',
      DELIVERY: 'Teslimat',
      INVOICING: 'Faturalama',
      PAYMENT: 'Ödeme',
    },
    statusMeanings: {
      UNKNOWN: 'Anlamı belirsiz',
      FULLY_DELIVERED: 'Kaynak başlığına göre tamamen teslim edilmiş',
      PAYMENT_OPEN: 'Açık',
      PAYMENT_PARTIAL: 'Kısmen ödenmiş',
      PAYMENT_PAID: 'Tamamen ödenmiş',
      PAYMENT_REVIEW: 'İnceleniyor',
      PAYMENT_WRITTEN_OFF: 'Tahsil edilemez',
    },
    kinds: {
      CORRECTION_INVOICE: 'Düzeltme faturası',
      INVOICE_UNCLASSIFIED: 'Fatura belgesi · türü belirsiz',
      OFFER: 'Teklif',
      ORDER: 'Sipariş',
      DELIVERY: 'İrsaliye',
      INVOICE: 'Fatura',
      CREDIT_NOTE: 'Alacak notu',
      CANCELLATION: 'İptal',
      PURCHASE_ORDER: 'Satın alma siparişi',
      PURCHASE_INVOICE: 'Alış faturası',
    },
  },
  es: {
    import: lexwareArchiveImportTranslations.es,
    nativeTitle: 'Documentos ERP',
    categoryTitle: 'Documentos importados',
    categoryDescription:
      'La búsqueda y las fechas también se aplican a los originales de esta categoría. Abra un documento para consultar su contenido protegido y sus archivos originales.',
    categoryOperativeFilters:
      'Los filtros de estado y pago solo se aplican a operaciones ERP. Los documentos importados conservan sus estados originales y no generan cuentas por cobrar actuales.',
    categorySort:
      'Los documentos importados se ordenan por fecha descendente. La ordenación por fecha o número se aplica a ambas listas.',
    noAccess: 'Se requiere permiso de lectura para los documentos históricos.',
    title: 'Documentos históricos',
    origin: 'Documento histórico · {{system}}',
    customerDescription:
      'Documentos originales de este socio de todos los años. Los importes históricos no aumentan las ventas actuales ni los saldos pendientes.',
    description: 'Contenido original de los documentos del sistema anterior.',
    readOnly:
      'Los documentos históricos están protegidos. Continuar una operación abierta requiere una transición revisada por separado.',
    search: 'Buscar número de documento',
    lexwareTitle: 'Documentos históricos de Lexware',
    lexwareReadOnly:
      'Archivo independiente de solo lectura. Sin vínculos con datos actuales de clientes, artículos o contabilidad; la consulta no crea operaciones ni mensajes.',
    lexwareSearch: 'Buscar número antiguo de cliente o documento, nombre o texto',
    allSources: 'Todos los sistemas de origen',
    partnerNumber: 'Número antiguo de cliente',
    historicalSourceTenant: 'Empresa de origen histórica',
    sourceTenantPlaceholder: 'Empresa de origen (p. ej. F3 / F4)',
    pdfFullText: 'Texto completo del PDF original',
    pdfTextStatus: 'Texto PDF',
    pdfTextExcerpt: 'Extracto de texto del PDF original',
    textStatuses: {
      EXTRACTED: 'Texto disponible',
      EMPTY: 'No se encontró texto',
      ERROR: 'No se pudo extraer el texto',
      TRUNCATED: 'Solo un extracto de texto',
    },
    pdfTextHint:
      'El texto extraído conserva los saltos de línea originales. Compruebe los datos ausentes o inciertos en el PDF original.',
    allKinds: 'Todos los tipos',
    kind: 'Tipo de documento',
    number: 'Número',
    date: 'Fecha del documento',
    partner: 'Socio histórico',
    status: 'Estado original',
    net: 'Neto',
    tax: 'Impuesto',
    gross: 'Bruto',
    loading: 'Cargando documentos históricos',
    error: 'No se pudieron cargar los documentos históricos.',
    retry: 'Volver a cargar',
    empty: 'No se encontraron documentos históricos.',
    reset: 'Restablecer filtros',
    close: 'Cerrar',
    help: 'Ayuda sobre documentos históricos',
    previous: 'Anterior',
    next: 'Siguiente',
    page: 'Página {{page}} de {{total}}',
    unknown: 'No registrado',
    emptyValue: 'Vacío',
    open: 'Abierto en el origen',
    closed: 'Cerrado en el origen',
    unknownOpen: 'Estado abierto sin clasificar',
    code: 'Código de estado {{code}}',
    items: 'Líneas',
    position: 'Posición',
    descriptionColumn: 'Descripción original',
    quantity: 'Cantidad',
    unit: 'Unidad',
    unitPrice: 'Precio unitario neto',
    discount: 'Descuento',
    fields: 'Campos personalizados históricos',
    fieldsRestricted: 'Los campos internos requieren permisos de lectura adicionales.',
    payments: 'Pagos históricos',
    paymentsRestricted: 'Los detalles de pago requieren permisos de lectura adicionales.',
    relations: 'Relaciones entre documentos',
    originals: 'Archivos originales y adjuntos',
    noOriginals: 'No hay ningún archivo original vinculado.',
    download: 'Descargar {{name}}',
    downloadError: 'No se pudo descargar el original.',
    downloaded: 'Original descargado.',
    original: 'Original',
    attachment: 'Adjunto de origen',
    emptyFile: 'Archivo de origen vacío',
    provenance: 'Prueba de origen',
    sourceSystem: 'Sistema de origen',
    sourceInstance: 'Instalación de origen',
    sourceTenant: 'Empresa de origen',
    sourceId: 'Identificador de origen',
    back: 'Todos los documentos históricos',
    headerTexts: 'Textos originales',
    openPartner: 'Abrir ficha del socio',
    statusDimensions: 'Dimensiones del estado de origen',
    paymentOpen: 'Estado de pago de origen: abierto',
    paymentClosed: 'Estado de pago de origen: liquidado o dado de baja',
    statusAxes: {
      CHANNEL: 'Canal de origen',
      DELIVERY: 'Entrega',
      INVOICING: 'Facturación',
      PAYMENT: 'Pago',
    },
    statusMeanings: {
      UNKNOWN: 'Significado sin resolver',
      FULLY_DELIVERED: 'Entregado completamente según la cabecera de origen',
      PAYMENT_OPEN: 'Abierto',
      PAYMENT_PARTIAL: 'Parcialmente pagado',
      PAYMENT_PAID: 'Totalmente pagado',
      PAYMENT_REVIEW: 'En revisión',
      PAYMENT_WRITTEN_OFF: 'Dado de baja',
    },
    kinds: {
      CORRECTION_INVOICE: 'Factura rectificativa',
      INVOICE_UNCLASSIFIED: 'Documento de facturación · tipo sin resolver',
      OFFER: 'Oferta',
      ORDER: 'Pedido',
      DELIVERY: 'Albarán',
      INVOICE: 'Factura',
      CREDIT_NOTE: 'Nota de crédito',
      CANCELLATION: 'Anulación',
      PURCHASE_ORDER: 'Pedido de compra',
      PURCHASE_INVOICE: 'Factura de proveedor',
    },
  },
};
